import {
  ACCOUNT_PROVIDERS,
  ADJUSTMENT_SUBCATEGORY_NAME,
  CARD_TRANSACTION_STATUS,
  CARD_TRANSACTION_TYPE,
  GENDERS,
  HISTORY_OPERATION_STATE_TYPE,
  MARITAL_STATUSES,
  MERK_ACTIVITY_DEPOSIT_WITHDRAWAL_STATUS,
  MERK_ACTIVITY_NON_TRADE_STATUS,
  MERK_ACTIVITY_ORDER_STATUS,
  MERK_ACTIVITY_TYPE,
  MerkActivity,
  OCCUPATIONS,
} from "@letsbit/malcolmlb";
import {
  PRISMA_PAYMENT_CARD_COLORS,
  PrismaColorSelectorPalette,
} from "@letsbit/milkylb";
import { useCallback } from "react";
import { defineMessages, useIntl } from "react-intl";
import { monthOrderedList } from "utils/months";

const scope = "hooks.use-translation-constants";

export const monthsMessages = defineMessages({
  january: {
    id: `${scope}.january`,
    defaultMessage: "Enero",
  },
  february: {
    id: `${scope}.february`,
    defaultMessage: "Febrero",
  },
  march: {
    id: `${scope}.march`,
    defaultMessage: "Marzo",
  },
  april: {
    id: `${scope}.april`,
    defaultMessage: "Abril",
  },
  may: {
    id: `${scope}.may`,
    defaultMessage: "Mayo",
  },
  june: {
    id: `${scope}.june`,
    defaultMessage: "Junio",
  },
  july: {
    id: `${scope}.july`,
    defaultMessage: "Julio",
  },
  august: {
    id: `${scope}.august`,
    defaultMessage: "Agosto",
  },
  september: {
    id: `${scope}.september`,
    defaultMessage: "Septiembre",
  },
  october: {
    id: `${scope}.october`,
    defaultMessage: "Octubre",
  },
  november: {
    id: `${scope}.november`,
    defaultMessage: "Noviembre",
  },
  december: {
    id: `${scope}.december`,
    defaultMessage: "Diciembre",
  },
  default: {
    id: `${scope}.default`,
    defaultMessage: "-",
  },
});

export const genderMessages = defineMessages({
  male: {
    id: `${scope}.male`,
    defaultMessage: "Varón / Masculino",
  },
  female: {
    id: `${scope}.female`,
    defaultMessage: "Mujer / Femenino",
  },
  other: {
    id: `${scope}.other`,
    defaultMessage: "X / Ninguna de las anteriores",
  },
  default: {
    id: `${scope}.default`,
    defaultMessage: "-",
  },
});
export const occupationMessages = defineMessages({
  unemployed: {
    id: `${scope}.unemployed`,
    defaultMessage: "Desemplead@",
  },
  student: {
    id: `${scope}.student`,
    defaultMessage: "Estudiante",
  },
  employed: {
    id: `${scope}.employed`,
    defaultMessage: "Emplead@",
  },
  retired: {
    id: `${scope}.retired`,
    defaultMessage: "Jubilad@",
  },
  self_employed: {
    id: `${scope}.self_employed`,
    defaultMessage: "Emplead@ Autónom@",
  },
  default: {
    id: `${scope}.default`,
    defaultMessage: "-",
  },
});

export const maritalStatusMessages = defineMessages({
  single: {
    id: `${scope}.single`,
    defaultMessage: "Solter@",
  },
  married: {
    id: `${scope}.married`,
    defaultMessage: "Casad@",
  },
  widowed: {
    id: `${scope}.widowed`,
    defaultMessage: "Viud@",
  },
  divorced: {
    id: `${scope}.divorced`,
    defaultMessage: "Divorciad@",
  },
  default: {
    id: `${scope}.default`,
    defaultMessage: "-",
  },
});

export const colorsMessages = defineMessages({
  [PRISMA_PAYMENT_CARD_COLORS.PINK]: {
    id: `${scope}.pink`,
    defaultMessage: "Rosa",
  },
  [PRISMA_PAYMENT_CARD_COLORS.CLASSIC]: {
    id: `${scope}.classic`,
    defaultMessage: "Clásico",
  },
  [PRISMA_PAYMENT_CARD_COLORS.GOLDEN]: {
    id: `${scope}.golden`,
    defaultMessage: "Dorado",
  },
  [PRISMA_PAYMENT_CARD_COLORS.LAVANDER]: {
    id: `${scope}.lavander`,
    defaultMessage: "Lavanda",
  },
  [PRISMA_PAYMENT_CARD_COLORS.GREEN]: {
    id: `${scope}.green`,
    defaultMessage: "Verde",
  },
  [PRISMA_PAYMENT_CARD_COLORS.ORANGE]: {
    id: `${scope}.orange`,
    defaultMessage: "Naranja",
  },
  [PRISMA_PAYMENT_CARD_COLORS.BLACK]: {
    id: `${scope}.black`,
    defaultMessage: "Negro",
  },
  [PRISMA_PAYMENT_CARD_COLORS.GREY]: {
    id: `${scope}.grey`,
    defaultMessage: "Gris",
  },
  default: {
    id: `${scope}.default`,
    defaultMessage: "-",
  },
});

export const adjustmentMessages = defineMessages({
  salary: {
    id: `${scope}.salary`,
    defaultMessage: "Sueldo",
  },
  bonus: {
    id: `${scope}.bonus`,
    defaultMessage: "Bono",
  },
  deposit_desk: {
    id: `${scope}.deposit_desk`,
    defaultMessage: "Depósito Desk",
  },
  withdraw_desk: {
    id: `${scope}.withdraw_desk`,
    defaultMessage: "Retiro Desk",
  },
  fee: {
    id: `${scope}.fee`,
    defaultMessage: "Honorarios",
  },
  adjustment: {
    id: `${scope}.adjustment`,
    defaultMessage: "Ajustes",
  },
  airdrop: {
    id: `${scope}.airdrop`,
    defaultMessage: "Airdrop",
  },
  migration: {
    id: `${scope}.migration`,
    defaultMessage: "Migración",
  },
  service_cost: {
    id: `${scope}.service_cost`,
    defaultMessage: "Costo de servicio",
  },
  gift: {
    id: `${scope}.gift`,
    defaultMessage: "Regalo",
  },
  cashback: {
    id: `${scope}.cashback`,
    defaultMessage: "Cashback",
  },
  promotion: {
    id: `${scope}.promotion`,
    defaultMessage: "Promoción",
  },
  compensation: {
    id: `${scope}.compensation`,
    defaultMessage: "Compensación",
  },
  default: {
    id: `${scope}.default`,
    defaultMessage: "Ajuste",
  },
});

export const cardTransactionMessages = defineMessages({
  purchase: {
    id: `${scope}.purchase`,
    defaultMessage: "Tarjeteaste",
  },
  purchaseFailed: {
    id: `${scope}.purchaseFailed`,
    defaultMessage: "Pago rechazado",
  },
  reversalPurchase: {
    id: `${scope}.reversalPurchase`,
    defaultMessage: "Pago devuelto",
  },
  withdrawal: {
    id: `${scope}.withdrawal`,
    defaultMessage: "Retiraste",
  },
  withdrawalFailed: {
    id: `${scope}.withdrawalFailed`,
    defaultMessage: "Retiro rechazado",
  },
  reversalWithdrawal: {
    id: `${scope}.reversalWithdrawal`,
    defaultMessage: "Retiro devuelto",
  },
  extraCash: {
    id: `${scope}.extraCash`,
    defaultMessage: "Retiro",
  },
  extraCashFailed: {
    id: `${scope}.extraCashFailed`,
    defaultMessage: "Retiro rechazado",
  },
  reversalExtraCash: {
    id: `${scope}.reversalExtraCash`,
    defaultMessage: "Retiro devuelto",
  },
  payment: {
    id: `${scope}.payment`,
    defaultMessage: "Cobraste",
  },
  paymentFailed: {
    id: `${scope}.paymentFailed`,
    defaultMessage: "Cobro rechazado",
  },
  reversalPayment: {
    id: `${scope}.reversalPayment`,
    defaultMessage: "Cobro devuelto",
  },
  refund: {
    id: `${scope}.refund`,
    defaultMessage: "Pago devuelto",
  },
  reversalRefund: {
    id: `${scope}.reversalRefund`,
    defaultMessage: "Ajuste",
  },
  void: {
    id: `${scope}.void`,
    defaultMessage: "Devolución",
  },
  default: {
    id: `${scope}.default`,
    defaultMessage: "Mov. de tarjeta",
  },
});

export const merkMessages = defineMessages({
  debit: {
    id: `${scope}.debit`,
    defaultMessage: "Fondeaste",
  },
  credit: {
    id: `${scope}.credit`,
    defaultMessage: "Rescataste",
  },
  debitMerk: {
    id: `${scope}.debitMerk`,
    defaultMessage: "Transferiste a tu cuenta comitente",
  },
  creditMerk: {
    id: `${scope}.creditMerk`,
    defaultMessage: "Rescataste desde tu cuenta comitente",
  },
  sell: {
    id: `${scope}.sell`,
    defaultMessage: "Vendiste {asset}",
  },
  buy: {
    id: `${scope}.buy`,
    defaultMessage: "Compraste {asset}",
  },
});

export const merkActivityStatusMessages = defineMessages({
  accepted: {
    id: `${scope}.accepted`,
    defaultMessage: "Aceptado",
  },
  rejected: {
    id: `${scope}.rejected`,
    defaultMessage: "Rechazado",
  },
  pending: {
    id: `${scope}.pending`,
    defaultMessage: "Pendiente",
  },
  //NON_TRADE
  executed: {
    id: `${scope}.executed`,
    defaultMessage: "Realizado",
  },
  corrected: {
    id: `${scope}.corrected`,
    defaultMessage: "Corregido",
  },
  cancelled: {
    id: `${scope}.cancelled`,
    defaultMessage: "Cancelado",
  },
  //ORDER
  partial: {
    id: `${scope}.partial`,
    defaultMessage: "Parcial",
  },
  completed: {
    id: `${scope}.completed`,
    defaultMessage: "Completada",
  },
  replaced: {
    id: `${scope}.replaced`,
    defaultMessage: "Reemplazada",
  },
  cancelling: {
    id: `${scope}.cancelling`,
    defaultMessage: "Cancelando",
  },
  replacing: {
    id: `${scope}.replacing`,
    defaultMessage: "Reemplazando",
  },
  created: {
    id: `${scope}.created`,
    defaultMessage: "Creada",
  },
  acceptedFem: {
    id: `${scope}.acceptedFem`,
    defaultMessage: "Aceptada",
  },
  pausedForDay: {
    id: `${scope}.pausedForDay`,
    defaultMessage: "Pausada por el día",
  },
  processing: {
    id: `${scope}.processing`,
    defaultMessage: "Procesando",
  },
  stopped: {
    id: `${scope}.stopped`,
    defaultMessage: "Detenida",
  },

  cancelledFem: {
    id: `${scope}.cancelledFem`,
    defaultMessage: "Cancelada",
  },
  rejectedFem: {
    id: `${scope}.rejectedFem`,
    defaultMessage: "Rechazada",
  },
  suspended: {
    id: `${scope}.suspended`,
    defaultMessage: "Suspendida",
  },
  calculating: {
    id: `${scope}.calculating`,
    defaultMessage: "Calculando",
  },
  expired: {
    id: `${scope}.expired`,
    defaultMessage: "Expirada",
  },
});

export const useTranslationConstants = (): {
  getGenders: (gender: GENDERS) => string;
  getMaritalStatus: (status: MARITAL_STATUSES) => string;
  getOccupations: (occupations: OCCUPATIONS) => string;
  getCardColors: (color: PrismaColorSelectorPalette) => string;
  getAdjustmentsOperations: (
    adjustment: ADJUSTMENT_SUBCATEGORY_NAME | undefined,
  ) => string;
  getCardTransactionOperations: (
    cardType: CARD_TRANSACTION_TYPE | undefined,
    status: HISTORY_OPERATION_STATE_TYPE | undefined,
    merchantName?: string,
  ) => string;
  getMonthNameFromNumber: (monthNumber: number) => string;
  getMerkOperations: (type: string, provider: ACCOUNT_PROVIDERS) => string;
  getMerkOrderTitle: (asset: string, type: string) => string;
  getMerkActivityStatus: (operation: MerkActivity) => {
    translate: string;
    color: "green" | "red" | "yellow";
  };
} => {
  const intl = useIntl();
  const getMonthNameFromNumber = (monthNumber: number) => {
    const monthKey = monthOrderedList[monthNumber];
    return intl.formatMessage(
      monthsMessages[monthKey] || monthsMessages.default,
    );
  };
  const getGenders = (gender: GENDERS) => {
    return intl.formatMessage(genderMessages[gender] || genderMessages.default);
  };
  const getMaritalStatus = (status: MARITAL_STATUSES) => {
    return intl.formatMessage(
      maritalStatusMessages[status] || maritalStatusMessages.default,
    );
  };
  const getOccupations = (occupations: OCCUPATIONS) => {
    return intl.formatMessage(
      occupationMessages[occupations] || occupationMessages.default,
    );
  };
  const getCardColors = (color: PrismaColorSelectorPalette) => {
    return intl.formatMessage(colorsMessages[color] || colorsMessages.default);
  };
  const getAdjustmentsOperations = (
    adjustment: ADJUSTMENT_SUBCATEGORY_NAME | undefined,
  ) => {
    return intl.formatMessage(
      (adjustment && adjustmentMessages[adjustment]) ??
        adjustmentMessages.default,
    );
  };

  const getMerkOperations = useCallback(
    (type: string, provider = ACCOUNT_PROVIDERS.ALPACA) => {
      switch (type) {
        case "credit":
          switch (provider) {
            case ACCOUNT_PROVIDERS.ALPACA:
              return intl.formatMessage(merkMessages.credit);
            case ACCOUNT_PROVIDERS.ESCO:
              return intl.formatMessage(merkMessages.creditMerk);
            default:
              return "";
          }
        case "debit":
          switch (provider) {
            case ACCOUNT_PROVIDERS.ALPACA:
              return intl.formatMessage(merkMessages.debit);
            case ACCOUNT_PROVIDERS.ESCO:
              return intl.formatMessage(merkMessages.debitMerk);
            default:
              return "";
          }
        default:
          return "";
      }
    },
    [intl],
  );

  const getCardTransactionOperations = useCallback(
    (
      cardType: CARD_TRANSACTION_TYPE | undefined,
      status: HISTORY_OPERATION_STATE_TYPE | undefined,
      merchantName?: string,
    ) => {
      switch (cardType) {
        case CARD_TRANSACTION_TYPE.PURCHASE:
          if (status === CARD_TRANSACTION_STATUS.APPROVED) {
            return (
              merchantName ??
              intl.formatMessage(cardTransactionMessages.purchase)
            );
          } else {
            return (
              merchantName ??
              intl.formatMessage(cardTransactionMessages.purchaseFailed)
            );
          }
        case CARD_TRANSACTION_TYPE.REVERSAL_PURCHASE:
        case CARD_TRANSACTION_TYPE.REVERSAL:
          return intl.formatMessage(cardTransactionMessages.reversalPurchase);
        case CARD_TRANSACTION_TYPE.WITHDRAWAL:
          if (status === CARD_TRANSACTION_STATUS.APPROVED) {
            return intl.formatMessage(cardTransactionMessages.withdrawal);
          } else {
            return intl.formatMessage(cardTransactionMessages.withdrawalFailed);
          }
        case CARD_TRANSACTION_TYPE.REVERSAL_WITHDRAWAL:
          return intl.formatMessage(cardTransactionMessages.reversalWithdrawal);
        case CARD_TRANSACTION_TYPE.EXTRACASH:
          if (status === CARD_TRANSACTION_STATUS.APPROVED) {
            return intl.formatMessage(cardTransactionMessages.extraCash);
          } else {
            return intl.formatMessage(cardTransactionMessages.extraCashFailed);
          }
        case CARD_TRANSACTION_TYPE.REVERSAL_EXTRACASH:
          return intl.formatMessage(cardTransactionMessages.reversalExtraCash);
        case CARD_TRANSACTION_TYPE.PAYMENT:
          if (status === CARD_TRANSACTION_STATUS.APPROVED) {
            return intl.formatMessage(cardTransactionMessages.payment);
          } else {
            return intl.formatMessage(cardTransactionMessages.paymentFailed);
          }
        case CARD_TRANSACTION_TYPE.REVERSAL_PAYMENT:
          return intl.formatMessage(cardTransactionMessages.reversalPayment);
        case CARD_TRANSACTION_TYPE.REFUND:
          return intl.formatMessage(cardTransactionMessages.refund);
        case CARD_TRANSACTION_TYPE.REVERSAL_REFUND:
          return intl.formatMessage(cardTransactionMessages.reversalRefund);
        case CARD_TRANSACTION_TYPE.VOID:
          return intl.formatMessage(cardTransactionMessages.void);
        default:
          return intl.formatMessage(cardTransactionMessages.default);
      }
    },
    [intl],
  );

  const getMerkOrderTitle = useCallback(
    (asset: string, type: string) => {
      switch (type) {
        case "sell":
          return intl.formatMessage(merkMessages.sell, { asset });
        case "buy":
          return intl.formatMessage(merkMessages.buy, { asset });
        default:
          return "";
      }
    },
    [intl],
  );

  const getMerkActivityStatus = useCallback(
    (
      operation: MerkActivity,
    ): {
      translate: string;
      color: "green" | "red" | "yellow";
    } => {
      if (
        operation.type === MERK_ACTIVITY_TYPE.DEPOSIT ||
        operation.type === MERK_ACTIVITY_TYPE.WITHDRAWAL
      ) {
        return operation.data.status ===
          MERK_ACTIVITY_DEPOSIT_WITHDRAWAL_STATUS.EXECUTED ||
          operation.data.status ===
            MERK_ACTIVITY_DEPOSIT_WITHDRAWAL_STATUS.CORRECT
          ? {
              translate: intl.formatMessage(
                merkActivityStatusMessages.accepted,
              ),
              color: "green",
            }
          : operation.data.status ===
                MERK_ACTIVITY_DEPOSIT_WITHDRAWAL_STATUS.REFUSED ||
              operation.data.status ===
                MERK_ACTIVITY_DEPOSIT_WITHDRAWAL_STATUS.CANCELED ||
              operation.data.status ===
                MERK_ACTIVITY_DEPOSIT_WITHDRAWAL_STATUS.REJECTED ||
              operation.data.status ===
                MERK_ACTIVITY_DEPOSIT_WITHDRAWAL_STATUS.DELETED
            ? {
                translate: intl.formatMessage(
                  merkActivityStatusMessages.rejected,
                ),
                color: "red",
              }
            : {
                translate: intl.formatMessage(
                  merkActivityStatusMessages.pending,
                ),
                color: "yellow",
              };
      } else if (operation.type === MERK_ACTIVITY_TYPE.ORDER) {
        switch (operation.data.status) {
          case MERK_ACTIVITY_ORDER_STATUS.PARTIALLY_FILLED:
            return {
              translate: intl.formatMessage(merkActivityStatusMessages.partial),
              color: "green",
            };
          case MERK_ACTIVITY_ORDER_STATUS.FILLED:
            return {
              translate: intl.formatMessage(
                merkActivityStatusMessages.completed,
              ),
              color: "green",
            };
          case MERK_ACTIVITY_ORDER_STATUS.REPLACED:
            return {
              translate: intl.formatMessage(
                merkActivityStatusMessages.replaced,
              ),
              color: "green",
            };
          case MERK_ACTIVITY_ORDER_STATUS.PENDING_CANCEL:
            return {
              translate: intl.formatMessage(
                merkActivityStatusMessages.cancelling,
              ),
              color: "yellow",
            };
          case MERK_ACTIVITY_ORDER_STATUS.PENDING_REPLACE:
            return {
              translate: intl.formatMessage(
                merkActivityStatusMessages.replacing,
              ),
              color: "yellow",
            };
          case MERK_ACTIVITY_ORDER_STATUS.NEW:
            return {
              translate: intl.formatMessage(merkActivityStatusMessages.created),
              color: "yellow",
            };
          case MERK_ACTIVITY_ORDER_STATUS.ACCEPTED:
            return {
              translate: intl.formatMessage(
                merkActivityStatusMessages.acceptedFem,
              ),
              color: "yellow",
            };
          case MERK_ACTIVITY_ORDER_STATUS.DONE_FOR_DAY:
            return {
              translate: intl.formatMessage(
                merkActivityStatusMessages.pausedForDay,
              ),
              color: "yellow",
            };
          case MERK_ACTIVITY_ORDER_STATUS.ACCEPTED_FOR_BIDDING:
            return {
              translate: intl.formatMessage(
                merkActivityStatusMessages.processing,
              ),
              color: "yellow",
            };
          case MERK_ACTIVITY_ORDER_STATUS.STOPPED:
            return {
              translate: intl.formatMessage(merkActivityStatusMessages.stopped),
              color: "yellow",
            };
          case MERK_ACTIVITY_ORDER_STATUS.CANCELED:
            return {
              translate: intl.formatMessage(
                merkActivityStatusMessages.cancelledFem,
              ),
              color: "yellow",
            };
          case MERK_ACTIVITY_ORDER_STATUS.REJECTED:
            return {
              translate: intl.formatMessage(
                merkActivityStatusMessages.rejectedFem,
              ),
              color: "red",
            };
          case MERK_ACTIVITY_ORDER_STATUS.SUSPENDED:
            return {
              translate: intl.formatMessage(
                merkActivityStatusMessages.suspended,
              ),
              color: "red",
            };
          case MERK_ACTIVITY_ORDER_STATUS.CALCULATED:
            return {
              translate: intl.formatMessage(
                merkActivityStatusMessages.calculating,
              ),
              color: "red",
            };
          case MERK_ACTIVITY_ORDER_STATUS.EXPIRED:
            return {
              translate: intl.formatMessage(merkActivityStatusMessages.expired),
              color: "red",
            };
          default:
            return { translate: "", color: "green" };
        }
      } else if (
        operation.type === MERK_ACTIVITY_TYPE.RECEIPT ||
        operation.type === MERK_ACTIVITY_TYPE.COUPON ||
        operation.type === MERK_ACTIVITY_TYPE.CHECKING_ACCOUNT_MOVEMENT
      ) {
        return { translate: "", color: "green" };
      } else {
        return operation.data.status === MERK_ACTIVITY_NON_TRADE_STATUS.EXECUTED
          ? {
              translate: intl.formatMessage(
                merkActivityStatusMessages.executed,
              ),
              color: "green",
            }
          : operation.data.status === MERK_ACTIVITY_NON_TRADE_STATUS.CANCELED
            ? {
                translate: intl.formatMessage(
                  merkActivityStatusMessages.cancelled,
                ),
                color: "red",
              }
            : {
                translate: intl.formatMessage(
                  merkActivityStatusMessages.corrected,
                ),
                color: "yellow",
              };
      }
    },
    [intl],
  );

  return {
    getGenders,
    getMaritalStatus,
    getOccupations,
    getCardColors,
    getAdjustmentsOperations,
    getCardTransactionOperations,
    getMonthNameFromNumber,
    getMerkOperations,
    getMerkOrderTitle,
    getMerkActivityStatus,
  };
};
